import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Loading from '../../../components/shared/Loading/Loading';
import Button from '../../../components/shared/Button/Button';
import Modal from '../../../components/shared/Modal/Modal';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import { useConfirm } from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormHint,
  FormActions,
  ErrorMessage,
} from '../../../components/shared/Form/Form.styles';
import {
  STAFF_ROLES,
  listStaff,
  createStaff,
  updateStaff,
  setStaffActivities,
  revokeStaff,
  reactivateStaff,
  listEmpresaActivities,
} from '../../../services/empresaStaff';
import { formatTimestamp } from '../../../utils/dates';
import {
  StaffContainer,
  StaffHeader,
  InfoBox,
  Notice,
  StaffList,
  StaffCard,
  StaffInfo,
  Pill,
  ChipRow,
  CardActions,
  EmptyState,
  ActivityPickerBox,
  PickerItem,
  RoleOption,
} from './Staff.styles';

const MAX_VISIBLE_CHIPS = 3;
const DEFAULT_MAX_ACTIVE = 50;

const sameIds = (a, b) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

// Lista de actividades con casillas. `options` = [{ id, title, status }].
const ActivityPicker = ({ options, selected, onChange, disabled }) => {
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <ActivityPickerBox>
      <div className="picker-bar">
        <span>{selected.length} de {options.length} seleccionadas</span>
        <span>
          <button type="button" disabled={disabled} onClick={() => onChange(options.map((o) => o.id))}>
            Todas
          </button>
          <button type="button" disabled={disabled} onClick={() => onChange([])}>
            Ninguna
          </button>
        </span>
      </div>
      {options.length === 0 ? (
        <div className="picker-empty">
          Aún no tienes actividades. Crea una en la sección Actividades para poder asignarla.
        </div>
      ) : (
        <div className="picker-list">
          {options.map((option) => (
            <PickerItem key={option.id}>
              <input
                type="checkbox"
                checked={selected.includes(option.id)}
                disabled={disabled}
                onChange={() => toggle(option.id)}
              />
              <span className="title">{option.title}</span>
              <StatusBadge status={option.status} />
            </PickerItem>
          ))}
        </div>
      )}
    </ActivityPickerBox>
  );
};

// Opciones del selector: las actividades de la empresa más las que el miembro
// ya tiene y no aparecen en el listado (para no perderlas al guardar).
const buildOptions = (activities, member) => {
  const options = activities.map((a) => ({ id: a.id, title: a.title, status: a.status }));
  const known = new Set(options.map((o) => o.id));
  ((member && member.activities) || []).forEach((a) => {
    if (!known.has(a.id)) options.push({ id: a.id, title: a.title, status: a.status });
  });
  return options;
};

// Traduce los errores del backend a mensajes claros; `invalidActivityIds` pide
// refrescar la lista de actividades.
const describeError = (error) => {
  if (error.data && Array.isArray(error.data.invalidActivityIds)) {
    return 'Alguna de las actividades elegidas ya no existe o no es de tu empresa. Actualizamos la lista; revisa tu selección.';
  }
  if (error.status === 401) {
    return 'Tu sesión de empresa venció. Vuelve a iniciar sesión.';
  }
  return error.message;
};

const StaffFormModal = ({ member, activities, onClose, onSaved, onInvalidActivities }) => {
  const editing = Boolean(member);
  const [form, setForm] = useState({
    name: member ? member.name : '',
    email: member ? member.email : '',
    password: '',
    role: member ? member.role : 'scanner',
  });
  const [selected, setSelected] = useState(member ? member.activities.map((a) => a.id) : []);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const options = buildOptions(activities, member);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let data;
      if (editing) {
        const payload = {};
        if (form.name.trim() !== member.name) payload.name = form.name.trim();
        if (form.email.trim() !== member.email) payload.email = form.email.trim();
        if (form.role !== member.role) payload.role = form.role;
        if (form.password) payload.password = form.password;
        if (!sameIds(selected, member.activities.map((a) => a.id))) payload.activityIds = selected;

        if (Object.keys(payload).length === 0) {
          onClose();
          return;
        }
        data = await updateStaff(member.id, payload);
        onSaved(
          data.staff,
          payload.password
            ? 'Cambios guardados. Cambiaste la contraseña: se cerraron todas sus sesiones.'
            : 'Cambios guardados.'
        );
      } else {
        data = await createStaff({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          role: form.role,
          activityIds: selected,
        });
        onSaved(data.staff, `${data.staff.name} ya puede entrar con su correo y contraseña.`);
      }
    } catch (err) {
      setError(describeError(err));
      if (err.data && Array.isArray(err.data.invalidActivityIds)) {
        const invalid = new Set(err.data.invalidActivityIds);
        setSelected((current) => current.filter((id) => !invalid.has(id)));
        onInvalidActivities();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? 'Editar miembro del equipo' : 'Nuevo miembro del equipo'}
      onClose={saving ? null : onClose}
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit}>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        <FormGroup>
          <FormLabel htmlFor="staff-name">Nombre</FormLabel>
          <FormInput
            id="staff-name"
            name="name"
            value={form.name}
            onChange={handleChange}
            maxLength={100}
            required
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="staff-email">Email</FormLabel>
          <FormInput
            id="staff-email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="off"
            required
          />
          <FormHint>Es su usuario para entrar. No puede repetirse con otro miembro del staff.</FormHint>
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="staff-password">
            {editing ? 'Nueva contraseña (opcional)' : 'Contraseña'}
          </FormLabel>
          <FormInput
            id="staff-password"
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            minLength={8}
            maxLength={128}
            autoComplete="new-password"
            required={!editing}
          />
          <FormHint>
            {editing
              ? 'Déjala vacía para no cambiarla. Si la cambias, se cierran todas sus sesiones.'
              : 'Mínimo 8 caracteres. Compártela con la persona por un medio seguro.'}
          </FormHint>
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="staff-role">Rol</FormLabel>
          <FormSelect id="staff-role" name="role" value={form.role} onChange={handleChange}>
            {Object.entries(STAFF_ROLES).map(([value, info]) => (
              <option key={value} value={value}>{info.label}</option>
            ))}
          </FormSelect>
          <RoleOption>{STAFF_ROLES[form.role] && STAFF_ROLES[form.role].description}</RoleOption>
        </FormGroup>

        <FormGroup>
          <FormLabel>Actividades asignadas</FormLabel>
          <ActivityPicker options={options} selected={selected} onChange={setSelected} disabled={saving} />
          <FormHint>Escanea (y, si es operador, vende) solo estas actividades. Sin ninguna no podrá hacer nada.</FormHint>
        </FormGroup>

        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear miembro'}
          </Button>
        </FormActions>
      </form>
    </Modal>
  );
};

const ActivitiesModal = ({ member, activities, onClose, onSaved, onInvalidActivities }) => {
  const [selected, setSelected] = useState(member.activities.map((a) => a.id));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const options = buildOptions(activities, member);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const data = await setStaffActivities(member.id, selected);
      onSaved(data.staff, 'Actividades asignadas actualizadas. Aplica en su siguiente escaneo.');
    } catch (err) {
      setError(describeError(err));
      if (err.data && Array.isArray(err.data.invalidActivityIds)) {
        const invalid = new Set(err.data.invalidActivityIds);
        setSelected((current) => current.filter((id) => !invalid.has(id)));
        onInvalidActivities();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Actividades de ${member.name}`} onClose={saving ? null : onClose} maxWidth="560px">
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      <FormHint style={{ marginTop: 0, marginBottom: 12 }}>
        Solo podrá escanear QRs de las actividades marcadas. Los cambios aplican de inmediato,
        sin que tenga que volver a iniciar sesión.
      </FormHint>
      <ActivityPicker options={options} selected={selected} onChange={setSelected} disabled={saving} />
      <FormActions>
        <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </FormActions>
    </Modal>
  );
};

const Staff = () => {
  const { empresa } = useEmpresaAuth();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();

  const [staff, setStaff] = useState([]);
  const [maxActive, setMaxActive] = useState(DEFAULT_MAX_ACTIVE);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null); // { text, error }
  const [busyId, setBusyId] = useState(null);
  const [formModal, setFormModal] = useState(null); // { member } (member null = alta)
  const [activitiesModal, setActivitiesModal] = useState(null); // member
  const [copied, setCopied] = useState(false);

  const loadActivities = useCallback(async () => {
    try {
      setActivities(await listEmpresaActivities());
    } catch (error) {
      // La pantalla sigue funcionando; el selector solo mostrará las ya asignadas.
      console.error('Error loading activities:', error);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await listStaff();
      setStaff(data.staff || []);
      if (data.maxActiveStaff) setMaxActive(data.maxActiveStaff);
    } catch (error) {
      setLoadError(describeError(error));
    } finally {
      setLoading(false);
    }
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    if (!empresa) {
      navigate('/empresa/login');
      return;
    }
    loadAll();
  }, [empresa, navigate, loadAll]);

  const replaceMember = (updated) => {
    setStaff((current) => {
      const exists = current.some((m) => m.id === updated.id);
      return exists
        ? current.map((m) => (m.id === updated.id ? updated : m))
        : [...current, updated];
    });
  };

  const handleSaved = (member, text) => {
    replaceMember(member);
    setFormModal(null);
    setActivitiesModal(null);
    setNotice({ text });
  };

  const handleRevoke = async (member) => {
    const ok = await confirm({
      title: `¿Revocar el acceso de ${member.name}?`,
      message:
        'Dejará de poder escanear de inmediato: su siguiente petición será rechazada y tendrá que ' +
        'iniciar sesión de nuevo. Puedes reactivarlo cuando quieras.',
      confirmLabel: 'Revocar acceso',
      danger: true,
    });
    if (!ok) return;
    setNotice(null);
    setBusyId(member.id);
    try {
      const data = await revokeStaff(member.id);
      replaceMember(data.staff);
      setNotice({ text: `Acceso de ${member.name} revocado.` });
    } catch (error) {
      setNotice({ text: describeError(error), error: true });
    } finally {
      setBusyId(null);
    }
  };

  const handleReactivate = async (member) => {
    setNotice(null);
    setBusyId(member.id);
    try {
      const data = await reactivateStaff(member.id);
      replaceMember(data.staff);
      setNotice({ text: `Acceso de ${member.name} reactivado. Debe volver a iniciar sesión.` });
    } catch (error) {
      setNotice({ text: describeError(error), error: true });
    } finally {
      setBusyId(null);
    }
  };

  const loginUrl = `${window.location.origin}/staff/login`;
  const copyLoginUrl = async () => {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setNotice({ text: 'No se pudo copiar. Selecciona el enlace y cópialo a mano.', error: true });
    }
  };

  if (!empresa) {
    return <Loading text="Cargando..." />;
  }

  const activeCount = staff.filter((m) => m.active).length;
  const limitReached = activeCount >= maxActive;

  return (
    <Layout headerType="empresa">
      <StaffContainer>
        <StaffHeader>
          <div>
            <h1>Equipo</h1>
            <p>
              Da acceso a tu personal para escanear QRs. {activeCount} de {maxActive} activos.
            </p>
          </div>
          <Button
            onClick={() => { setNotice(null); setFormModal({ member: null }); }}
            disabled={loading || limitReached}
            title={limitReached ? `Máximo ${maxActive} miembros activos` : undefined}
          >
            + Nuevo miembro
          </Button>
        </StaffHeader>

        <InfoBox>
          <strong>Cada miembro solo escanea las actividades que le asignes.</strong> Un{' '}
          <strong>escáner</strong> únicamente valida QRs; un <strong>operador</strong> también puede
          crear ventas en persona y ver las que él mismo registró. Solo tú (el dueño) puedes anular una venta;
          el staff no puede rechazar reservas.
          <div className="login-link">
            <span>Dónde inician sesión:</span>
            <code>{loginUrl}</code>
            <Button type="button" size="small" variant="secondary" onClick={copyLoginUrl}>
              {copied ? '¡Copiado!' : 'Copiar enlace'}
            </Button>
          </div>
        </InfoBox>

        {notice && <Notice $error={notice.error} role="status">{notice.text}</Notice>}

        {loading ? (
          <Loading text="Cargando equipo..." />
        ) : loadError ? (
          <EmptyState>
            <h3>No pudimos cargar tu equipo</h3>
            <p>{loadError}</p>
            <Button onClick={loadAll}>Reintentar</Button>
          </EmptyState>
        ) : staff.length === 0 ? (
          <EmptyState>
            <h3>Aún no tienes miembros en tu equipo</h3>
            <p>Crea el primero y asígnale las actividades que podrá escanear.</p>
            <Button onClick={() => setFormModal({ member: null })}>+ Nuevo miembro</Button>
          </EmptyState>
        ) : (
          <StaffList>
            {staff.map((member) => {
              const role = STAFF_ROLES[member.role];
              const shown = member.activities.slice(0, MAX_VISIBLE_CHIPS);
              const hidden = member.activities.length - shown.length;
              const busy = busyId === member.id;
              return (
                <StaffCard key={member.id} $revoked={!member.active}>
                  <StaffInfo>
                    <div className="name-row">
                      <h3>{member.name}</h3>
                      <Pill>{role ? role.label : member.role}</Pill>
                      {member.active ? (
                        <Pill $color="var(--success-dark)" $bg="var(--success-subtle)">Activo</Pill>
                      ) : (
                        <Pill $color="var(--danger-dark)" $bg="var(--danger-subtle)">Revocado</Pill>
                      )}
                    </div>
                    <div className="email">{member.email}</div>

                    <ChipRow>
                      {member.activities.length === 0 ? (
                        <Pill $color="var(--warning-dark)" $bg="var(--warning-subtle)">
                          Sin actividades: no puede escanear nada
                        </Pill>
                      ) : (
                        <>
                          {shown.map((a) => (
                            <Pill key={a.id} $color="var(--text-secondary)" $bg="var(--bg-section)" title={a.title}>
                              {a.title}
                            </Pill>
                          ))}
                          {hidden > 0 && (
                            <Pill $color="var(--text-secondary)" $bg="var(--bg-section)">
                              +{hidden} más
                            </Pill>
                          )}
                        </>
                      )}
                    </ChipRow>

                    <div className="meta">
                      {member.active
                        ? `Último acceso: ${member.lastLoginAt ? formatTimestamp(member.lastLoginAt) : 'nunca'}`
                        : `Revocado el ${formatTimestamp(member.revokedAt)}`}
                    </div>
                  </StaffInfo>

                  <CardActions>
                    <Button
                      size="small"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => { setNotice(null); setFormModal({ member }); }}
                    >
                      Editar
                    </Button>
                    {member.active && (
                      <Button
                        size="small"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => { setNotice(null); setActivitiesModal(member); }}
                      >
                        Actividades
                      </Button>
                    )}
                    {member.active ? (
                      <Button size="small" variant="danger" disabled={busy} onClick={() => handleRevoke(member)}>
                        {busy ? 'Revocando…' : 'Revocar'}
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="success"
                        disabled={busy || limitReached}
                        onClick={() => handleReactivate(member)}
                      >
                        {busy ? 'Reactivando…' : 'Reactivar'}
                      </Button>
                    )}
                  </CardActions>
                </StaffCard>
              );
            })}
          </StaffList>
        )}
      </StaffContainer>

      {formModal && (
        <StaffFormModal
          member={formModal.member}
          activities={activities}
          onClose={() => setFormModal(null)}
          onSaved={handleSaved}
          onInvalidActivities={loadActivities}
        />
      )}
      {activitiesModal && (
        <ActivitiesModal
          member={activitiesModal}
          activities={activities}
          onClose={() => setActivitiesModal(null)}
          onSaved={handleSaved}
          onInvalidActivities={loadActivities}
        />
      )}
      {dialog}
    </Layout>
  );
};

export default Staff;
